import React, { useEffect, useRef, useState } from "react";
import { getUserProfile, updateUserProfile } from "../../requests";
import {
  deleteAuthToken,
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
  notifyError,
  notifySuccess,
} from "../../utils/helper";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useNavigate } from "react-router-dom";
import DeleteAccountPopup from "../common/DeleteAccountPopup";

const SettingsUI = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [step, setStep] = useState(0);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const [sameEmail, setSameEmail] = useState(false);
  const [profileimg, setprofileimg] = useState("");
  const [deleteAccountPopup, setDeleteAccountPopup] = useState(false);
  const [file, setFile] = useState(null);

  const notify_visibility = (timer) => {
    if (isNotificationVisible) return;

    setIsNotificationVisible(true);

    setTimeout(() => {
      setIsNotificationVisible(false);
    }, timer);
  };

  const getProfile = async () => {
    dispatch(startLoader());
    try {
      const profileRes = await getUserProfile();
      setName(profileRes.data.user.name);
      setEmail(profileRes.data.user.email);
      setPhone(profileRes.data.user.phone_number);
      setprofileimg(profileRes.data.user.profile);
    } catch (error) {
      if (error?.response?.status == 403) {
        deleteAuthToken();

        notifyError(
          error?.response?.data?.error || "Your session has expired. Please log in again."
        );

        navigate("/login");

        return;
      }
      notifyError(
        error?.response?.data?.error ||
          "Failed to fetch profile information. Please try again later."
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const getOtpToOldEmail = async () => {
    if (!validate()) return;

    dispatch(startLoader());
    try {
      const otpRes = await updateUserProfile({ name, email, phone });

      setSameEmail(otpRes.data.same_email);

      if (otpRes.status < 400) {
        setStep(1); // Move to the next step after successfully sending OTP
      }
    } catch (error) {
      if (error?.response?.status == 403) {
        deleteAuthToken();

        notifyError(
          error?.response?.data?.error || "Session Time out log in again"
        );

        navigate("/login");

        return;
      }
      notifyError(
        error?.response?.data?.error ||
          "Something went wrong. Please try again later."
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const getOtpToNewEmail = async () => {
    if (!validate()) return;
    dispatch(startLoader());


    try {

      const formData = new FormData();
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("otp", otp);
      formData.append("file", file);
      const otpRes = await updateUserProfile(formData);

      if (otpRes.status < 400 && sameEmail) {
        notifySuccess(otpRes?.data?.message || "Profile updated successfully!");
        setStep(0); // Reset to the initial step after successful update
        setOtp(""); // Clear OTP input
        setNewOtp(""); // Clear new OTP input
        getProfile();
      }

      if (otpRes.status < 400 && !sameEmail) {
        setStep(2); // Move to the next step after successfully sending OTP to new email
      }
    } catch (error) {
      if (error?.response?.status == 403) {
        deleteAuthToken();

        notifyError(
          error?.response?.data?.error || "Session Time out log in again"
        );

        navigate("/login");

        return;
      }
      notifyError(
        error?.response?.data?.error ||
          "Failed to send OTP to old email. Please try again later."
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const updateProfile = async () => {
    if (!validate()) return;

    dispatch(startLoader());
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("otp", otp);
      formData.append("newOtp", newOtp);
      formData.append("file", file);

      const updateRes = await updateUserProfile(formData);

      if (updateRes.status < 400) {

        if (updateRes.data.newEmailUpdated) {
          //login again
          deleteAuthToken();
          navigate("/login");
          notifySuccess("Email updated successfully! Please log in again.");
          return;
        }


        notifySuccess(
          updateRes?.data?.message || "Profile updated successfully!"
        );
        setStep(0); // Reset to the initial step after successful update
        setOtp(""); // Clear OTP input
        setNewOtp(""); // Clear new OTP input
        getProfile(); // Fetch the updated profile information

       
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.error ||
          "Failed to update profile. Please try again later."
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleLogout = () => {
    deleteAuthToken();
    navigate("/login");
    notifySuccess("Logged out successfully!");
  };

  const validate = () => {
    if (isNotificationVisible) return;

    notify_visibility(2000);

    if (!name) {
      notifyError("Please enter a name.");
      return false;
    }

    if (!isValidName(name)) {
      notifyError(
        "Please enter a valid name. Name should be at least 3 characters long and should not contain numbers or special characters."
      );
      return false;
    }

    if (!email) {
      notifyError("Please enter an email address");
      return false;
    }

    if (!isValidEmail(email)) {
      notifyError("Please enter a valid email address.");
      return false;
    }

    if (!phone) {
      notifyError("Please enter a phone number.");
      return false;
    }

    if (!isValidPhoneNumber(phone)) {
      notifyError("Please enter a valid phone number.");
      return false;
    }

    if (step == 1 && !otp) {
      notifyError("Please enter the OTP sent to your old email.");
      return false;
    }

    if (step == 1 && otp.length !== 6) {
      notifyError("Please enter OTP of 6 digits long.");
      return false;
    }

    if (step == 2 && !newOtp) {
      notifyError("Please enter the OTP sent to your new email.");
      return false;
    }

    if (step == 2 && newOtp.length !== 6) {
      notifyError("Please enter OTP of 6 digits long.");
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setprofileimg(URL.createObjectURL(e.target.files[0]));
  };

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <div className="settings-main">
      <div className="settings-form-con">
        <input
          type="file"
          style={{ display: "none" }}
          onChange={handleFileChange}
          ref={fileRef}
        />
        {step == 0 && (
          <div className="settings-form-img pe-cursor">
            <img
            style={{objectFit:'cover'}}
              src={profileimg}
              width="100%"
              height="100%"
              onClick={() => fileRef.current.click()}
            />
          </div>
        )}
        <div className="settings-form-w">
          {step == 0 && (
            <div className="settings-form-form">
              <div className="settings-form-field">
                <label className="settings-form-label">Name</label>
                <input
                  type="text"
                  placeholder="Name"
                  className="settings-form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="settings-form-field">
                <label className="settings-form-label">Email</label>
                <input
                  type="text"
                  placeholder="example@domain.com"
                  className="settings-form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="settings-form-field">
                <label className="settings-form-label">Phone</label>
                <input
                  type="number"
                  placeholder="Phone"
                  className="settings-form-input no-spinner"
                  onInput={(e) => {
                    if (e.target.value.length > 10) {
                      e.target.value = e.target.value.slice(0, 10);
                    }
                  }}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={true}
                  style={{
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #D1D5DB",
                    color: "#1F2937",
                    cursor: "not-allowed",
                  }}
                />
              </div>

              <div className="di-flex">
                <button
                  className="my-btn"
                  style={{ width: "75%" }}
                  onClick={getOtpToOldEmail}
                >
                  Update
                </button>
                <button
                  onClick={handleLogout}
                  className="my-btn"
                  style={{
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #D1D5DB",
                    color: "#1F2937",
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          {step == 1 && (
            <div className="settings_form-form">
              <div className="login-header-text">Verify email</div>
              <div
                className="login-header-subtext"
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "50px",
                }}
              >
                We have sent a verification code to your old email. Please enter
                the code below to verify your account.
              </div>
              <div className="settings-form-field">
                <label className="settings-form-label">Code</label>
                <input
                  type="number"
                  placeholder="Code"
                  className="settings-form-input no-spinner"
                  onInput={(e) => {
                    if (e.target.value.length > 6) {
                      e.target.value = e.target.value.slice(0, 6);
                    }
                  }}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </div>

              <button
                className="my-btn"
                style={{ width: "100%", marginTop: "40px" }}
                onClick={getOtpToNewEmail}
              >
                Update
              </button>
            </div>
          )}

          {step == 2 && (
            <div className="settings_form-form">
              <div className="login-header-text">Verify new email</div>
              <div
                className="login-header-subtext"
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "50px",
                }}
              >
                We have sent a verification code to your new email. Please enter
                the code below to verify your account.
              </div>
              <div className="settings-form-field">
                <label className="settings-form-label">Code</label>
                <input
                  type="number"
                  placeholder="Code"
                  className="settings-form-input no-spinner"
                  onInput={(e) => {
                    if (e.target.value.length > 6) {
                      e.target.value = e.target.value.slice(0, 6);
                    }
                  }}
                  value={newOtp}
                  onChange={(e) => setNewOtp(e.target.value)}
                />
              </div>

              <button
                className="my-btn"
                style={{ width: "100%", marginTop: "40px" }}
                onClick={updateProfile}
              >
                Update
              </button>
            </div>
          )}

          {step == 0 && (
            <div
              className="settings-delete-acc pe-cursor"
              onClick={() => setDeleteAccountPopup(true)}
            >
              Delete Account
            </div>
          )}

          {deleteAccountPopup && (
            <DeleteAccountPopup fn={() => setDeleteAccountPopup(false)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsUI;
